import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Plus, FolderPlus } from 'lucide-react';
import { useSessionStore } from '@/stores';
import { sessionApi } from '@/services/api';

interface SaveSessionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  saveAsMode?: boolean; // If true, always creates a new session (Save As)
}

export function SaveSessionModal({ open, onOpenChange, saveAsMode = false }: SaveSessionModalProps) {
  const [sessionName, setSessionName] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [folders, setFolders] = useState<Array<{ folder_id: string; folder_name: string }>>([]);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [selectKey, setSelectKey] = useState(0);

  const chatMessages = useSessionStore(state => state.chatMessages);
  const sessionId = useSessionStore(state => state.sessionId);
  const sessionHasBeenSaved = useSessionStore(state => state.sessionHasBeenSaved);
  const savedFolderForSession = useSessionStore(state => state.savedFolderForSession);

  const setSessionId = useSessionStore(state => state.setSessionId);
  const updateSessionName = useSessionStore(state => state.setSessionName);
  const setSessionHasBeenSaved = useSessionStore(state => state.setSessionHasBeenSaved);
  const setSavedFolderForSession = useSessionStore(state => state.setSavedFolderForSession);

  // Fetch folders when modal opens
  useEffect(() => {
    if (open) {
      loadFolders();
      // Pre-select the current folder if session has been saved
      if (savedFolderForSession) {
        setSelectedFolder(savedFolderForSession);
      }
    }
  }, [open, savedFolderForSession]);

  const loadFolders = async () => {
    try {
      const folderList = await sessionApi.fetchFolders();
      console.log('Loaded folders:', folderList);
      setFolders(folderList);
    } catch (error) {
      console.error('Failed to load folders:', error);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      setError('Please enter a folder name.');
      return;
    }

    setIsCreatingFolder(true);
    setError('');

    try {
      const newFolder = await sessionApi.createFolder(newFolderName);
      console.log('Created new folder:', newFolder);

      // Poll for the new folder with retries
      let folderFound = false;
      let attempts = 0;
      const maxAttempts = 8;

      while (!folderFound && attempts < maxAttempts) {
        console.log(`Polling for folder (attempt ${attempts + 1}/${maxAttempts})`);

        // Fetch folders from server
        const folderList = await sessionApi.fetchFolders();
        console.log('Fetched folders:', folderList);
        console.log('Looking for folder_id:', newFolder.folder_id);

        // Check if new folder exists in the list
        const foundFolder = folderList.find(f => f.folder_id === newFolder.folder_id);

        if (foundFolder) {
          console.log('New folder found in list!', foundFolder);
          setFolders(folderList);
          folderFound = true;
        } else {
          console.log('New folder not found yet, waiting...');
          attempts++;
          if (attempts < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, 500)); // Wait 500ms before retry
          }
        }
      }

      if (!folderFound) {
        console.warn('New folder not found after max attempts, but will display anyway');
        // Still load whatever folders we have
        const folderList = await sessionApi.fetchFolders();
        setFolders(folderList);
      }

      // Select the newly created folder
      console.log('Selecting folder:', newFolder.folder_id);
      setSelectedFolder(newFolder.folder_id);

      // Force Select component to re-render
      setSelectKey(prev => prev + 1);

      setNewFolderName('');
      setShowNewFolderInput(false);
    } catch (err: any) {
      console.error('Failed to create folder:', err);
      setError(err?.message || 'Failed to create folder. Please try again.');
    } finally {
      setIsCreatingFolder(false);
    }
  };

  const handleSave = async () => {
    if (!sessionName.trim()) {
      setError('Please enter a session name.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Update session context to include current active drafts before saving
      const sessionContextStr = localStorage.getItem('session_context');
      const sessionContext = sessionContextStr ? JSON.parse(sessionContextStr) : {};
      const activeDraftsStr = localStorage.getItem('active_drafts');
      const activeDrafts = activeDraftsStr ? JSON.parse(activeDraftsStr) : [];
      sessionContext.current_drafts = activeDrafts;
      localStorage.setItem('session_context', JSON.stringify(sessionContext));
      console.log('[SaveSessionModal] Saving session with drafts:', activeDrafts);

      const folderToSave = selectedFolder || undefined;

      if (saveAsMode) {
        // Save As: Always create a new session
        const response = await sessionApi.addSession(
          sessionName,
          chatMessages,
          folderToSave
        );

        if (response.session_id) {
          setSessionId(response.session_id);
        }
      } else if (sessionHasBeenSaved && sessionId) {
        // Save: Update existing session
        await sessionApi.saveSession(
          sessionId,
          sessionName,
          chatMessages,
          folderToSave
        );
      } else {
        // First time save: Create new session
        const response = await sessionApi.addSession(
          sessionName,
          chatMessages,
          folderToSave
        );

        if (response.session_id) {
          setSessionId(response.session_id);
        }
      }

      updateSessionName(sessionName);
      setSessionHasBeenSaved(true);
      setSavedFolderForSession(folderToSave || null);
      setSessionName('');
      setSelectedFolder(null);

      // Dispatch event to update session list in sidebar
      window.dispatchEvent(new Event('sessions-updated'));

      onOpenChange(false);
    } catch (err: any) {
      console.error('Failed to save session:', err);
      setError(err?.message || 'Failed to save session. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSessionName('');
    setError('');
    setSelectedFolder(null);
    setShowNewFolderInput(false);
    setNewFolderName('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{saveAsMode ? 'Save As' : 'Save Session'}</DialogTitle>
          <DialogDescription>
            {saveAsMode
              ? 'Save a copy of the current session with a new name.'
              : 'Save your current session to continue later.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="session-name">Session Name</Label>
            <Input
              id="session-name"
              placeholder="Enter session name"
              value={sessionName}
              onChange={(e) => setSessionName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isLoading && !showNewFolderInput) {
                  handleSave();
                }
              }}
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="folder-select">Folder (Optional)</Label>
            <div className="flex gap-2">
              <Select
                key={selectKey}
                value={selectedFolder || 'none'}
                onValueChange={(value) => setSelectedFolder(value === 'none' ? null : value)}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue>
                    {selectedFolder
                      ? folders.find(f => f.folder_id === selectedFolder)?.folder_name || 'Select folder'
                      : 'No folder'}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No folder</SelectItem>
                  {folders.map((folder) => (
                    <SelectItem key={folder.folder_id} value={folder.folder_id}>
                      {folder.folder_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setShowNewFolderInput(!showNewFolderInput)}
                title="Create new folder"
              >
                <FolderPlus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {showNewFolderInput && (
            <div className="space-y-2 pl-2 border-l-2 border-blue-200">
              <Label htmlFor="new-folder-name">New Folder Name</Label>
              <div className="flex gap-2">
                <Input
                  id="new-folder-name"
                  placeholder="Enter folder name"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !isCreatingFolder) {
                      handleCreateFolder();
                    }
                  }}
                  disabled={isCreatingFolder}
                />
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  onClick={handleCreateFolder}
                  disabled={isCreatingFolder}
                >
                  {isCreatingFolder ? 'Creating...' : 'Create'}
                </Button>
              </div>
            </div>
          )}

          {error && (
            <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg border border-red-200">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : 'Save Session'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
